import { useState } from "react";

import { Select } from "antd";
import { IS_DEV_ENV } from "constants/app.constants";
import styled from "styled-components";

import { getTownshipByDls, getTownshipByNts } from "api/map";

import { BaseInput } from "components/base";
import FieldErrorMessage from "components/base/FieldErrorMessage";

import { validateDLS } from "../utils/validateDLS";
import { JumpActionRow, jumpPanelStyles } from "./jumpPanelLayout";

const initialTownshipValues = {
  DLS: { township: null, range: null, meridian: null },
  NTS: { block: null, sheet: null, mus: null, grid: null },
  Box: { boxDimensions: null }
};

interface TownshipFieldDescription {
  name: string;
  placeholder: string;
  /** How validateDLS names this field in what it rejects. */
  invalidName?: string;
  /**
   * Set where the placeholder is a format hint rather than the field's name, so there
   * is no name to draw above the control.
   */
  hideLabel?: boolean;
  type?: string;
  min?: string;
  max?: string;
}

// One field per entry, in the order each survey system asks for them.
const townshipFields: Record<string, TownshipFieldDescription[]> = {
  DLS: [
    {
      name: "township",
      placeholder: "Township",
      invalidName: "Township",
      type: "number",
      min: "1",
      max: "126"
    },
    {
      name: "range",
      placeholder: "Range",
      invalidName: "Range",
      type: "number",
      min: "1",
      max: "34"
    },
    {
      name: "meridian",
      placeholder: "Meridian",
      invalidName: "Meridian",
      type: "number",
      min: "1",
      max: "6"
    }
  ],
  NTS: [
    { name: "block", placeholder: "Block" },
    { name: "sheet", placeholder: "Sheet", type: "number", min: "0", max: "120" },
    { name: "mus", placeholder: "MUS" },
    { name: "grid", placeholder: "Grid", type: "number", min: "1", max: "16" }
  ],
  Box: [
    {
      name: "boxDimensions",
      type: "text",
      hideLabel: true,
      placeholder:
        "Formats: [-105.2,41.3], [-105.1,41.4] or -105.2,41.3,-105.1,41.4 or many others"
    }
  ]
};

const UNABLE_TO_FIND = "Unable to find location";

/**
 * Where a place found by township lands. Shared with the Lat/Long jump, which
 * aims at a point rather than a block and had nothing of its own to scale to.
 */
export const PLACE_JUMP_ZOOM = 11;

interface MapTownshipSelectorProps {
  closeMenu: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  goToPointAndHighlightPolygon: (data: any, zoom?: number) => void;
}

export default function MapTownshipSelector({
  closeMenu,
  goToPointAndHighlightPolygon
}: MapTownshipSelectorProps) {
  const [canSubmitTownship, setCanSubmitTownship] = useState(false);
  const [townshipErrors, setTownshipErrors] = useState("");
  const [townshipOption, setTownshipOption] = useState("DLS");
  const [townshipValues, setTownshipValues] = useState(initialTownshipValues);
  const [invalidFields, setInvalidFields] = useState([]);

  function parseBoxDimensions(boxString) {
    if (!boxString) return null;

    try {
      const cleanInput = boxString.trim();
      if (!cleanInput) return null;

      const isValidCoordinate = (lat, lon) => {
        return (
          !isNaN(lat) &&
          !isNaN(lon) &&
          lat >= -90 &&
          lat <= 90 &&
          lon >= -180 &&
          lon <= 180
        );
      };
      const extractNumbers = (input) => {
        const numberRegex = /-?\d+(?:\.\d+)?/g;
        const matches = input.match(numberRegex);
        return matches ? matches.map((n) => parseFloat(n)).filter((n) => !isNaN(n)) : [];
      };

      let coordinates = [];

      const pairFormats = [
        /[[()\]{}]\s*(-?\d+(?:\.\d+)?)\s*[,\s]+\s*(-?\d+(?:\.\d+)?)\s*[[()\]{}]/g,
        /(-?\d+(?:\.\d+)?)\s*[,\s]+\s*(-?\d+(?:\.\d+)?)/g
      ];

      for (const regex of pairFormats) {
        const matches = [];
        let match;
        while ((match = regex.exec(cleanInput)) !== null) {
          const num1 = parseFloat(match[1]);
          const num2 = parseFloat(match[2]);
          matches.push([num1, num2]);
        }

        if (matches.length >= 2) {
          for (const [a, b] of matches) {
            if (isValidCoordinate(a, b)) {
              coordinates.push([b, a]);
            } else if (isValidCoordinate(b, a)) {
              coordinates.push([a, b]);
            }
          }
          if (coordinates.length >= 2) break;
        }
      }
      if (coordinates.length < 2) {
        const numbers = extractNumbers(cleanInput);
        if (numbers.length === 4) {
          const [a, b, c, d] = numbers;
          if (isValidCoordinate(b, a) && isValidCoordinate(d, c)) {
            coordinates = [
              [a, b],
              [c, d]
            ];
          } else if (isValidCoordinate(a, b) && isValidCoordinate(c, d)) {
            coordinates = [
              [b, a],
              [d, c]
            ];
          }
        } else if (numbers.length === 6) {
          const pairs = [];
          for (let i = 0; i < numbers.length - 1; i += 2) {
            const a = numbers[i];
            const b = numbers[i + 1];
            if (isValidCoordinate(a, b)) {
              pairs.push([b, a]);
            } else if (isValidCoordinate(b, a)) {
              pairs.push([a, b]);
            }
          }
          if (pairs.length >= 2) coordinates = pairs;
        } else if (numbers.length >= 8 && numbers.length % 2 === 0) {
          const pairs = [];
          for (let i = 0; i < numbers.length - 1; i += 2) {
            const a = numbers[i];
            const b = numbers[i + 1];
            if (isValidCoordinate(a, b)) {
              pairs.push([b, a]);
            } else if (isValidCoordinate(b, a)) {
              pairs.push([a, b]);
            }
          }
          if (pairs.length >= 2) coordinates = pairs;
        }
      }

      if (coordinates.length < 2) {
        const wktMatch = cleanInput.match(/POLYGON\s*\(\s*\(\s*(.*?)\s*\)\s*\)/i);
        if (wktMatch) {
          const coordString = wktMatch[1];
          const coordPairs = coordString
            .split(/[,;]/)
            .map((pair) => {
              const nums = extractNumbers(pair);
              if (nums.length >= 2) {
                const [a, b] = nums;
                if (isValidCoordinate(b, a)) return [a, b];
                if (isValidCoordinate(a, b)) return [b, a];
              }
              return null;
            })
            .filter(Boolean);

          if (coordPairs.length >= 2) coordinates = coordPairs;
        }

        const geoJsonMatch = cleanInput.match(/coordinates\s*:\s*\[\s*(.*?)\s*\]/i);
        if (geoJsonMatch && coordinates.length < 2) {
          const coordString = geoJsonMatch[1];
          const numbers = extractNumbers(coordString);

          if (numbers.length >= 4 && numbers.length % 2 === 0) {
            const pairs = [];
            for (let i = 0; i < numbers.length - 1; i += 2) {
              const [lon, lat] = [numbers[i], numbers[i + 1]];
              if (isValidCoordinate(lat, lon)) {
                pairs.push([lon, lat]);
              }
            }
            if (pairs.length >= 2) coordinates = pairs;
          }
        }
      }

      if (coordinates.length < 2) return null;

      const validCoords = coordinates.filter(([lon, lat]) => isValidCoordinate(lat, lon));
      if (validCoords.length < 2) return null;

      const lons = validCoords.map((coord) => coord[0]);
      const lats = validCoords.map((coord) => coord[1]);

      const bounds = {
        minLon: Math.min(...lons),
        maxLon: Math.max(...lons),
        minLat: Math.min(...lats),
        maxLat: Math.max(...lats)
      };
      const lonDiff = bounds.maxLon - bounds.minLon;
      const latDiff = bounds.maxLat - bounds.minLat;
      if (lonDiff < 0.001 && latDiff < 0.001) return null;

      return bounds;
    } catch (error) {
      return null;
    }
  }

  function canSubmitTownshipForm(values, option) {
    if (option === "Box") {
      return values.Box.boxDimensions && values.Box.boxDimensions.trim() !== "";
    }

    return Object.keys(values[option]).every((val) => {
      return (
        values[option][val] && values[option][val] !== initialTownshipValues[option][val]
      );
    });
  }

  function handleTownshipOptionChange(newOption) {
    setTownshipOption(newOption);
    setTownshipErrors("");
    setInvalidFields([]);
    const canSubmit = canSubmitTownshipForm(townshipValues, newOption);
    setCanSubmitTownship(canSubmit);
  }

  function commitTownshipValues(optionValues) {
    const newValues = { ...townshipValues, [townshipOption]: optionValues };
    setInvalidFields([]);
    setTownshipErrors("");
    setTownshipValues(newValues);
    const canSubmit = canSubmitTownshipForm(newValues, townshipOption);
    setCanSubmitTownship(canSubmit);
  }

  function handleTownshipChange(prop, val) {
    commitTownshipValues({ ...townshipValues[townshipOption], [prop]: val });
  }

  function clearTownshipValues() {
    commitTownshipValues({ ...initialTownshipValues[townshipOption] });
  }

  function onTownshipKeyDown(event, closeMenu) {
    if (event.key === "Enter" && canSubmitTownship) {
      gotoTownship(closeMenu);
    }
  }

  async function gotoTownship(closeMenu = null) {
    let location = null;
    setTownshipErrors("");
    setInvalidFields([]);
    try {
      if (townshipOption === "Box") {
        const boxDimensions = parseBoxDimensions(townshipValues.Box.boxDimensions);

        if (!boxDimensions) {
          const input = townshipValues.Box.boxDimensions?.trim();
          if (!input) {
            setTownshipErrors("Please enter coordinates");
          } else {
            const simpleCoords = input
              .split(",")
              .map((c) => parseFloat(c.trim()))
              .filter((n) => !isNaN(n));
            if (simpleCoords.length === 4) {
              setTownshipErrors(
                "Coordinates are out of valid range or do not form a meaningful area"
              );
            } else if (simpleCoords.length > 0 && simpleCoords.length !== 4) {
              setTownshipErrors(
                "For box selection, provide exactly 4 coordinates: minLon, minLat, maxLon, maxLat"
              );
            } else if (input.includes("[")) {
              const coordPairs = input.match(
                /\[\s*(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)\s*\]/g
              );
              if (!coordPairs || coordPairs.length < 2) {
                setTownshipErrors(
                  "Need at least 2 coordinate pairs to define a box area"
                );
              } else {
                setTownshipErrors(
                  "Coordinates are out of valid range or do not form a meaningful area"
                );
              }
            } else {
              setTownshipErrors(
                "Invalid coordinate format. Use: minLon, minLat, maxLon, maxLat"
              );
            }
          }
          return;
        }

        const { minLat, maxLat, minLon, maxLon } = boxDimensions;

        const bounds = {
          extent: {
            minx: minLon,
            miny: minLat,
            maxx: maxLon,
            maxy: maxLat
          },
          exactBounds: true
        };

        goToPointAndHighlightPolygon(bounds);
        closeMenu && closeMenu();
        return;
      }
      if (townshipOption === "DLS") {
        const errors = validateDLS(townshipValues.DLS);
        if (errors.length > 0) {
          setInvalidFields(errors);
          setTownshipErrors("Unable to find location");
          return;
        }
        location = await getTownshipByDls(townshipValues.DLS);
      }
      if (townshipOption === "NTS") location = await getTownshipByNts(townshipValues.NTS);
      if (!location.data) {
        setTownshipErrors("Unable to find location");
        return;
      }
      goToPointAndHighlightPolygon(location.data, PLACE_JUMP_ZOOM);
      closeMenu();
    } catch (err) {
      setTownshipErrors(UNABLE_TO_FIND);
    }
  }

  const fields = townshipFields[townshipOption] ?? [];
  const optionValues = townshipValues[townshipOption];

  return (
    <TownshipContainer>
      <div className="township-form">
        <TownshipOptionSelector
          data-testid="jump-option-select"
          value={townshipOption}
          options={[
            { value: "DLS", label: "DLS" },
            { value: "NTS", label: "NTS" },
            ...(IS_DEV_ENV ? [{ value: "Box", label: "Box" }] : [])
          ]}
          onChange={handleTownshipOptionChange}
        />
        <div className="township-fields">
          {fields.map((field) => {
            // A row of filled-in boxes shows nothing but values, so a field that holds
            // one keeps its placeholder as a name above it. The label is the placeholder
            // word again rather than a second copy of it, so the two cannot come apart.
            const filled = Boolean(optionValues[field.name]);

            return (
              <div
                key={field.name}
                className={filled ? "township-field filled" : "township-field"}>
                {!field.hideLabel && (
                  <span className="field-label">{field.placeholder}</span>
                )}
                <BaseInput
                  placeholder={field.placeholder}
                  type={field.type}
                  min={field.min}
                  max={field.max}
                  value={optionValues[field.name] ?? ""}
                  className={
                    field.invalidName && invalidFields.includes(field.invalidName)
                      ? "has-errors"
                      : ""
                  }
                  onKeyDown={(e) => onTownshipKeyDown(e, closeMenu)}
                  onChange={(val) => handleTownshipChange(field.name, val)}
                />
              </div>
            );
          })}
        </div>
      </div>
      <JumpActionRow
        idPrefix="jump-option"
        canSubmit={canSubmitTownship}
        onClear={clearTownshipValues}
        onSubmit={() => gotoTownship(closeMenu)}
      />
      {townshipErrors.length > 0 && (
        <div className="flex-row justify-center">
          <FieldErrorMessage message={townshipErrors} />
        </div>
      )}
    </TownshipContainer>
  );
}

const TownshipContainer = styled.div`
  ${jumpPanelStyles}

  /* The option select and the fields it chooses are one group, so a label stands in
     the row it belongs to rather than over the row above. This is the distance the
     other tab keeps between a control and the name of the one under it. */
  .township-form {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .township-fields {
    display: flex;
    gap: 8px;
    /* A set that cannot fit at the width its fields are left with takes a second line.
       Nothing here is allowed to push the panel wider than it is and put a sideways
       scrollbar under it. */
    flex-wrap: wrap;

    .township-field {
      /* A field is its name over its own control, so it is the pair and not the input
         that takes a place in the row. The fields of an option share the row evenly and
         shrink with it, rather than each holding a width of its own. The smallest one
         still readable is what decides when the row wraps instead. */
      flex: 1 1 0;
      min-width: 72px;
    }
  }
  /* A field is as tall as the select above it, which is as tall as the ones the other
     tab stacks. */
  .BaseInput .input {
    height: 32px;
    padding: 6px 8px;
  }
`;

const TownshipOptionSelector = styled(Select)`
  width: 100%;
`;
